import _ from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { managerAPI } from '@/shared/api';
import {
  ADD_NEW_EVENT_TYPE_BEAT_HR_TAB_OPTIONS,
  KEY_CANCEL,
  KEY_RECOIL,
  LIST_EVENT_SKIP_ON_RUN_EVENT,
  LIST_RUN_EVENT,
  PREVENT_AFIB_OVERRIDE_EVENT_LIST,
  RHYTHM_EVENT_LIST_CAN_EDIT,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEvent,
} from '@/shared/config';
import {
  convertToMomentJs,
  getEventDuration,
  getEventDurationInSecond,
  isRrInterval,
} from '@/shared/lib/aiUtils';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE,
  ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE,
  ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE,
  ADD_EVENT_NO_BEAT_ERROR_MESSAGE,
  UPDATE_EVENT_NO_DATA_ERROR_MESSAGE,
  generateAddEventExistErrorMessage,
  getBeatCountAddUpdateEvents,
  getEventBothSideAddUpdateEvents,
  isCheckContinueData,
  updateEventChangeAndNew,
} from '@/entities/event';
import { NO_BEATS_LOW_DURATION_ARTIFACT_ERROR_MESSAGE } from '@/widgets/beats/ui/handlerConstants';
import { logError } from '@/shared/lib/apiHelpers';
import { getDataRange, isRangesOverlap } from '@/shared/lib/timeRange';
import {
  selectActiveTab,
  selectBeatChanges,
  selectControlModal,
  selectEventChanges,
  selectEventNews,
  selectPickedEndDate,
  selectPickedStartDate,
  useReportStore,
} from '@/entities/report';
import type { AddNewEventProps } from './addNewEvent';

/**
 * Drives the add-new-event modal of the beats tab, covering the date and time form, its validation and the persistence of a new AFib or Artifact event. Binds tab-1 report-store selectors and setters for events, event options, picked dates and the selected strip, holds the date, time, event type, error and saving fields in merged local state, syncs them from the picked range on change, and exposes the change handlers plus an add action routing to the AFib or single-event path.
 *
 * @param props - Modal inputs: the loaded ECG data map, timezone, study and profile ids, the report start and stop and the onCancel callback.
 * @returns The active button, the form state and the onChangeEvent, onChangeCheckbox, onChange, onClickCancel and onClickAdd handlers.
 */
const useAddNewEvent = ({
  onCancel,
  ecgDataMap,
  timezone,
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  stopReport,
}: AddNewEventProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the newly created events for the beats tab into the report store. Forwards the value to the store action under the tab-1 key.
   *
   * @param v - New list of new events, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the events newly created on the beats tab without subscribing the component to them. Applies the tab-1 new-events selector to a one-off snapshot of the report store.
   *
   * @returns The new events queued for the beats tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending event edits for the beats tab into the report store. Forwards the value to the store action under the tab-1 key.
   *
   * @param v - New event-change list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  /**
   * Reads the pending edits to existing events for the beats tab without subscribing the component to them. Applies the tab-1 event-changes selector to a one-off snapshot of the report store.
   *
   * @returns The event changes queued for the beats tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the pending beat edits for the beats tab without subscribing the component to them. Applies the tab-1 beat-changes selector to a one-off snapshot of the report store.
   *
   * @returns The beat changes queued for the beats tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the strip currently selected on the beats tab into the report store. Forwards the value to the store action under the tab-1 key.
   *
   * @param v - New selected event, beat or ECG bookmark, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Writes the start of the picked range for the beats tab into the report store. Forwards the value to the store action under the tab-1 key.
   *
   * @param v - New start timestamp, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Writes the end of the picked range for the beats tab into the report store. Forwards the value to the store action under the tab-1 key.
   *
   * @param v - New end timestamp, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  /**
   * Reads the active beat-tab button without subscribing the component to it. Applies the tab-1 active-tab selector to a one-off snapshot of the report store.
   *
   * @returns The active rhythm event, beat event or beat-HR tab currently selected.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  const setCombinedEventsInHeatmap = useReportStore((s) => s.actions.setCombinedEventsInHeatmap);
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the event options snapshot used by undo and redo grouping into the report store. Forwards the value to the store action under the tab-1 key.
   *
   * @param v - New event options, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);

  const [state, setState] = useMergeState<{
    startDate: any;
    startTime: any;
    endDate: any;
    endTime: any;
    eventType: any;
    errorObject: any;
    isSaving: boolean;
    isUse100Afib: boolean;
  }>({
    startDate: undefined,
    startTime: undefined,
    endDate: undefined,
    endTime: undefined,
    eventType: ADD_NEW_EVENT_TYPE_BEAT_HR_TAB_OPTIONS[1],
    errorObject: {},
    isSaving: false,
    isUse100Afib: false,
  });

  /**
   * Records the option chosen in one of the add-event form dropdowns. Stores the option under the given field name and leaves validation state untouched.
   *
   * @param name - Form field being set, such as eventType.
   * @param option - Selected option object.
   * @returns Nothing.
   */
  const onChangeEvent = (name: string, option: any) => {
    setState({ [name]: option });
  };

  /**
   * Toggles a checkbox on the add-event form, notably the whole-report AFib option. Stores the value under the given field name and, when it is turned on, widens the picked range to the report start and stop.
   *
   * @param name - Checkbox field being set, such as isUse100Afib.
   * @param value - New checked value.
   * @returns Nothing.
   */
  const onChangeCheckbox = (name: string, value: any) => {
    setState({ [name]: value });
    if (value) {
      setPickedStartDate(startReport);
      setPickedEndDate(stopReport);
    }
  };

  /**
   * Records a newly picked date or time on the add-event form. Stores the value under the given field name and clears any outstanding validation errors.
   *
   * @param name - Form field being set, such as startDate or endTime.
   * @param time - Picked moment value.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any) => {
    setState({
      [name]: time,
      errorObject: {},
    });
  };

  /**
   * Abandons the add-event form and hands control back to the parent modal. Clears the validation errors and saving flag, then calls the onCancel prop with the flag saying whether the new-event dialog should reopen.
   *
   * @param isOpenNewEvent - Whether the parent should reopen the new-event dialog after closing.
   * @returns Nothing.
   */
  const onClickCancel = (isOpenNewEvent?: boolean) => {
    setState({
      errorObject: {},
      isSaving: false,
    });
    onCancel(isOpenNewEvent);
  };

  /**
   * Reports a rejected add-event attempt to the user and marks the time inputs invalid. Raises an error toast with the given message, then writes back a copy of the error object with startTime and endTime set to Invalid value and clears the saving flag.
   *
   * @param toastMessage - Message shown in the error toast.
   * @returns Nothing.
   */
  const handleSetError = (toastMessage: string = '') => {
    toastrError(toastMessage, 'Error');
    const clonedErrorObject = { ...state.errorObject };
    _.assign(clonedErrorObject, {
      startTime: 'Invalid value',
      endTime: 'Invalid value',
    });
    setState({
      errorObject: clonedErrorObject,
      isSaving: false,
    });
  };

  /**
   * Resolves how a drafted event interacts with the events already covering its range, before it is stored. Skips pairs protected from AFib override, and for every overlap either merges same-type events by widening the draft, rejects a draft contained in an older event, or trims the neighbour to the draft boundary and re-checks its remaining beats and duration so short leftovers are deleted rather than resized.
   *
   * @param combinedEvents - Existing events overlapping the drafted range, with pending changes already applied.
   * @param newEvent - Draft event, whose start and stop may be widened in place when merging.
   * @param localBeatChanges - Unsaved beat additions and deletions counted when re-measuring a trimmed event.
   * @returns A promise resolving to the link events to persist alongside the draft, plus the blocking event or error message when the draft is refused.
   */
  const processEvent = async ({
    combinedEvents,
    newEvent,
    localBeatChanges,
  }: {
    combinedEvents: any;
    newEvent: any;
    localBeatChanges: any;
  }) => {
    const newEventStart = new Date(newEvent.start).getTime();
    const newEventStop = new Date(newEvent.stop).getTime();
    // * Chứa những event có liên quan tới event vừa tạo
    const linkEvents: any[] = [];
    let errorEvent;
    let errorMessages;
    // **  Được add những event: Artifact, Afib, VT, SVT, 1 Block, 2 Block, 3 Block
    // Can add artifact if no beat
    // Rule add new event
    // 2 event cùng loại:
    // Giao nhau + mới chứa cũ: event mới và cũ hợp thành 1. Lấy even mới và delete event cũ.Khi xóa event mới đi thì revert event cũ khỏi state Delete
    // Mới trong cũ: ko tạo dc event mới. Báo toast
    // 2 event khác loại
    // Giao nhau: nó sẽ tạo ra 2 cái event luôn, với event mới thì theo cái đã chọn, còn event cũ điểm start time của nó là end time event kia. Hoặc là end time của event cũ sẽ là start time của event mới.
    // Mới chứa cũ: event mới xóa cũ. Khi xóa event mới đi thì revert event cũ khỏi state Delete
    // Mới trong cũ: ko tạo dc event mới. Báo toast
    // **
    // **
    // Riêng đối với afib:
    // Khi vắt qua 'No data' thì sẽ tách ra những event không chứa Nodata đó
    // Tất cả event bị override nhưng ngoại trừ các event liên quan đến beat V, VT và Pause và artifact.
    // **
    const combinedEventsLength = combinedEvents.length;
    for (let i = 0; i < combinedEventsLength; i += 1) {
      const event = combinedEvents[i];
      const eventType = event.type === TYPE_BEAT_ENUM.DELETED ? event.oldType : event.type;
      if (
        PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(eventType) &&
        newEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB
      ) {
        continue;
      }
      if (
        PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(newEvent.type) &&
        eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
      ) {
        continue;
      }
      const eventStart = new Date(event.start).getTime();
      const eventStop = new Date(event.stop).getTime();
      if (isRangesOverlap(eventStart, eventStop, newEventStart, newEventStop)) {
        const isSameType = eventType === newEvent.type;
        const isNewEventContainOldEvent = eventStart >= newEventStart && eventStop <= newEventStop;
        const isNewEventInsideOldEvent = newEventStart >= eventStart && newEventStop <= eventStop;
        if (isSameType) {
          if (isNewEventContainOldEvent) {
            console.log('[beatHr/addNewEvent]-OVERLAP-sametype-isNewEventContainOldEvent');
            if (
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              linkEvents.push(event);
            }
          } else if (isNewEventInsideOldEvent) {
            console.log(
              '[beatHr/addNewEvent]-OVERLAP-sametype-isNewEventInsideOldEvent',
              event,
              newEvent,
            );
            errorEvent = event;
            break;
          } else {
            console.log('[beatHr/addNewEvent]-OVERLAP-sametype-intersect', event);
            if (
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              linkEvents.push(event);
            } else if (event.isNew) {
              linkEvents.push(event);
            }
            //* Đổi start stop của event mới là hợp của cả 2 event
            newEvent.start = moment(Math.min(eventStart, newEventStart)).toISOString();
            newEvent.stop = moment(Math.max(eventStop, newEventStop)).toISOString();
          }
        } else if (
          (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(eventType) &&
            LIST_RUN_EVENT.includes(newEvent.type)) ||
          (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(newEvent.type) &&
            LIST_RUN_EVENT.includes(eventType))
        ) {
          //* VT/SVT không cần check overlap cho 1 số event type khác
          continue;
        } else if (
          (!isNewEventContainOldEvent &&
            newEvent.type === TYPE_RHYTHM_EVENT_ENUM.SVT &&
            eventType === TYPE_BEAT_EVENT_ENUM.SVE_RUN) ||
          (newEvent.type === TYPE_RHYTHM_EVENT_ENUM.VT && eventType === TYPE_BEAT_EVENT_ENUM.VE_RUN)
        ) {
          if (isNewEventInsideOldEvent) {
            errorEvent = event;
            break;
          }
          console.log('[rhythmEvents/addNewEvent]-OVERLAP-sametype-intersect', {
            newEvent,
            event,
            eventType,
          });
          //* Giao nhau
          linkEvents.push(event);
          //* Đổi start stop của event mới là hợp của cả 2 event
          newEvent.start = moment(Math.min(eventStart, newEventStart)).toISOString();
          newEvent.stop = moment(Math.max(eventStop, newEventStop)).toISOString();
        } else if (RHYTHM_EVENT_LIST_CAN_EDIT.includes(eventType)) {
          if (isNewEventContainOldEvent) {
            console.log('[beatHr/addNewEvent]-OVERLAP-difftype-isNewEventContainOldEvent');
            if (
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              linkEvents.push(event);
            }
          } else if (isNewEventInsideOldEvent) {
            console.log('[beatHr/addNewEvent]-OVERLAP-difftype-isNewEventInsideOldEvent');
            if (
              newEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
              eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              errorMessages = ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE;
              break;
            } else if (
              newEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT &&
              eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
            ) {
              errorMessages = ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE;
              break;
            }
            errorEvent = event;
            break;
          } else {
            if (
              eventType !== TYPE_RHYTHM_EVENT_ENUM.AFIB ||
              eventType !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
            ) {
              continue;
            }
            console.log('[beatHr/addNewEvent]-OVERLAP-difftype-intersect');
            let currentEventStart = event.start;
            let currentEventStop = event.stop;
            //* nếu artifact event sau khi cắt chỉ còn 1 beat => xét duration nếu lớn hơn 10s thì cắt được, nếu nhỏ hơn 10s thì delete artifact event
            //* nếu afib,vt,svt,1st,2nd,3rd,tachy,brady,pause event sau khi cắt chỉ còn 1 beat => xóa luôn
            if (eventStart < newEventStart && newEventStart < eventStop) {
              //* event mới nằm bên trái
              currentEventStop = newEvent.start;
            }
            if (eventStart < newEventStop && newEventStop < eventStop) {
              //* event mới nằm bên phải
              currentEventStart = newEvent.stop;
            }

            // * Lấy mảng beat hiện tại bao gồm những beat đã thay đổi để check
            const { mergeBeatCount } = await getBeatCountAddUpdateEvents({
              start: currentEventStart,
              stop: currentEventStop,
              samplingFrequency: ecgDataMap.samplingFrequency,
              localBeatChanges,
              ecgDataMap,
            });
            // *
            const newDuration = getEventDuration(currentEventStart, currentEventStop);
            const newDurationInSec = getEventDurationInSecond(currentEventStart, currentEventStop);
            if (eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
              if (newDurationInSec >= 5) {
                linkEvents.push({
                  ...event,
                  start: currentEventStart,
                  stop: currentEventStop,
                  duration: newDuration,
                  canChangeDuration: true,
                });
              } else {
                linkEvents.push(event);
              }
            } else if (mergeBeatCount === 1) {
              if (newDurationInSec >= 10 && eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
                linkEvents.push({
                  ...event,
                  start: currentEventStart,
                  stop: currentEventStop,
                  duration: newDuration,
                  canChangeDuration: true,
                });
              } else {
                linkEvents.push(event);
              }
            } else if (mergeBeatCount === 0) {
              linkEvents.push(event);
            } else {
              //* Mảng beat lớn hơn 1
              linkEvents.push({
                ...event,
                start: currentEventStart,
                stop: currentEventStop,
                duration: newDuration,
                canChangeDuration: true,
              });
            }
          }
        }
      }
    }
    return { linkEvents, errorEvent, errorMessages };
  };

  /**
   * Validates and stores a new AFib event drawn over the loaded ECG data. Clamps the range to the available data and rejects it when there is no data, fewer than two beats or under five seconds between them, snaps the bounds onto the surrounding beats, refetches the neighbouring holter events, resolves overlaps per new event through processEvent, then pushes the event plus its link events into the cloned new-event and event-change lists and commits them with the undo grouping.
   *
   * @param newEvent - Draft AFib event carrying id, start, stop, type, channel and the isNew flag.
   * @returns A promise resolving once the event has been stored and the modal closed, or the attempt rejected with an error toast.
   */
  const handleAddNewEventAfib = async (newEvent: any) => {
    const momentStart = moment(newEvent.start);
    const momentStop = moment(newEvent.stop);
    const dataRange = getDataRange(ecgDataMap, momentStart, momentStop);
    if (_.isEmpty(dataRange)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return;
    }
    // * Resize duration to fit data range (để event không mắc qua vùng no data)
    const startData = dataRange[0].start;
    const stopData = dataRange[dataRange.length - 1].stop;
    if (startData > momentStart) {
      newEvent.start = moment(startData).toISOString();
    }
    if (stopData < momentStop) {
      newEvent.stop = moment(stopData).toISOString();
    }
    //* Check duration > 5
    if (getEventDurationInSecond(newEvent.start, newEvent.stop) < 5) {
      handleSetError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE);
      return;
    }
    const localBeatChanges = getBeatChanges().filter(
      (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
    );
    const { mergeBeatCount, diffBeatBySecond, startBeat, stopBeat } =
      await getBeatCountAddUpdateEvents({
        start: newEvent.start,
        stop: newEvent.stop,
        samplingFrequency: ecgDataMap.samplingFrequency,
        localBeatChanges,
        ecgDataMap,
      });
    if (mergeBeatCount < 2) {
      handleSetError(ADD_EVENT_NO_BEAT_ERROR_MESSAGE);
      return;
    }
    if (diffBeatBySecond < 5) {
      handleSetError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE);
      return;
    }
    if (startBeat && stopBeat) {
      newEvent.start = moment(startBeat).toISOString();
      newEvent.stop = moment(stopBeat).toISOString();
    }
    const { channels } = ecgDataMap;

    const newEvents: any[] = [];
    if (isCheckContinueData(dataRange, newEvent)) {
      newEvents.push(newEvent);
    } else {
      newEvents.push({
        id: moment(newEvent.start).valueOf(),
        start: newEvent.start,
        stop: newEvent.stop,
        type: TYPE_RHYTHM_EVENT_ENUM.AFIB,
        channel: channels.length === 3 ? 2 : 1,
        isNew: true,
      });

      //* Split event when over no data range
      // let eventStart = newEvent.start;
      // for (let i = 0; i < dataRange.length; i += 1) {
      //   if (i === dataRange.length - 1) {
      //     newEvents.push({
      //       id: moment(eventStart).valueOf(),
      //       start: eventStart,
      //       stop: newEvent.stop,
      //       type: TYPE_RHYTHM_EVENT_ENUM.AFIB,
      //       channel: 2,
      //       isNew: true,
      //     });
      //   } else if (!moment(dataRange[i].stop).isSame(moment(dataRange[i + 1].start))) {
      //     newEvents.push({
      //       id: moment(eventStart).valueOf(),
      //       start: eventStart,
      //       stop: dataRange[i].stop.toISOString(),
      //       type: TYPE_RHYTHM_EVENT_ENUM.AFIB,
      //       channel: 2,
      //       isNew: true,
      //     });
      //     eventStart = dataRange[i + 1]?.start.toISOString();
      //   }
      // }
    }
    // fetch event to check
    const startSearchDate = newEvents[0].start;
    const stopSearchDate = newEvents[newEvents.length - 1].stop;
    const holterEventsFilter = {
      studyId,
      profileId,
      types: typesPersistEvent,
      startSearchDate,
      stopSearchDate,
      isUse100Afib: state.isUse100Afib,
    };

    const dataFormat = await getEventBothSideAddUpdateEvents(holterEventsFilter);
    const combinedEvents = updateEventChangeAndNew(
      dataFormat,
      getEventChanges(),
      getEventNews(),
      startSearchDate,
      stopSearchDate,
      typesPersistEvent,
    );
    //
    const clonedEventNews = _.cloneDeep(getEventNews());
    const clonedEventChanges = _.cloneDeep(getEventChanges());
    for (let i = 0; i < newEvents.length; i += 1) {
      const currentNewEvent = newEvents[i];
      const localBeatChanges = getBeatChanges().filter(
        (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
      );
      const { linkEvents, errorEvent, errorMessages } = await processEvent({
        combinedEvents,
        newEvent: currentNewEvent,
        localBeatChanges,
      });
      if (errorEvent) {
        handleSetError(generateAddEventExistErrorMessage(errorEvent, timezoneOffset));
        return;
      }
      if (errorMessages) {
        handleSetError(errorMessages);
        return;
      }
      currentNewEvent.startTimeEpoch = moment(currentNewEvent.start).startOf('minutes').valueOf();
      currentNewEvent.stopTimeEpoch = moment(currentNewEvent.stop).endOf('minutes').valueOf();
      currentNewEvent.duration = getEventDuration(currentNewEvent.start, currentNewEvent.stop);
      if (linkEvents.length) {
        linkEvents.forEach((x) => {
          if (x.isNew) {
            _.remove(clonedEventNews, (eventNew) => eventNew.id === x.id);
          } else if (x.canChangeDuration) {
            const foundEventChanged = clonedEventChanges.find(
              (eventChange) => eventChange.idEvent === x.idEvent,
            );
            if (foundEventChanged) {
              _.assign(foundEventChanged, {
                start: x.start,
                stop: x.stop,
                duration: x.duration,
              });
            } else {
              delete x.canChangeDuration;
              clonedEventChanges.push(x);
            }
          } else {
            // * Gán type delete cho những event được link với new event
            const foundEventChanged = clonedEventChanges.find(
              (eventChange) => eventChange.idEvent === x.idEvent,
            );
            if (foundEventChanged) {
              _.assign(foundEventChanged, { type: TYPE_BEAT_ENUM.DELETED, oldType: x.oldType });
            }
            // else {
            //   clonedEventChanges.push({
            //     ...x,
            //     type: TYPE_BEAT_ENUM.DELETED,
            //     oldType: x.oldType,
            //   });
            // }
          }
        });
        currentNewEvent.linkEvents = linkEvents.filter((x) => !x.isNew);
        clonedEventNews.push(currentNewEvent);
      } else {
        clonedEventNews.push(currentNewEvent);
      }
    }

    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: clonedEventNews,
      eventChangesState: clonedEventChanges,
    }));

    setEventChanges(clonedEventChanges);
    setEventNews(clonedEventNews);
    onClickCancel(true);
  };

  /**
   * Validates and stores a single new Artifact event over one continuous stretch of ECG data. Rejects ranges with no data or a gap, requires at least two beats for artifacts shorter than ten seconds, refetches the neighbouring holter events and resolves overlaps through processEvent, then appends the event with its link events, refreshes the heat-map events when an RR interval tab is active, selects the strip on the artifact tab and commits the change lists with the undo grouping.
   *
   * @param newEvent - Draft artifact event carrying id, start, stop, type, channel and the isNew flag.
   * @returns A promise resolving once the event has been stored and the modal closed, or the attempt rejected with an error toast.
   */
  const handleAddNewEventSingle = async (newEvent: any) => {
    const momentStart = moment(newEvent.start);
    const momentStop = moment(newEvent.stop);
    const dataRange = getDataRange(ecgDataMap, momentStart, momentStop);
    if (_.isEmpty(dataRange)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return;
    }
    // * Resize duration to fit data range (để event không mắc qua vùng no data)
    const startData = dataRange[0].start;
    const stopData = dataRange[dataRange.length - 1].stop;
    if (startData > momentStart) {
      newEvent.start = moment(startData).toISOString();
    }
    if (stopData < momentStop) {
      newEvent.stop = moment(stopData).toISOString();
    }
    if (!isCheckContinueData(dataRange, newEvent)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return;
    }

    const localBeatChanges = getBeatChanges().filter(
      (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
    );
    const newEventDuration = getEventDurationInSecond(newEvent.start, newEvent.stop);
    if (newEventDuration < 10 && newEvent?.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
      const { mergeBeatCount } = await getBeatCountAddUpdateEvents({
        start: newEvent.start,
        stop: newEvent.stop,
        samplingFrequency: ecgDataMap.samplingFrequency,
        localBeatChanges,
        ecgDataMap,
      });
      if (mergeBeatCount < 2) {
        handleSetError(NO_BEATS_LOW_DURATION_ARTIFACT_ERROR_MESSAGE);
        return;
      }
    }

    const clonedEventNews = _.cloneDeep(getEventNews());
    const clonedEventChanges = _.cloneDeep(getEventChanges());
    // let combinedEvents = getCombinedEvents();
    const dateContainEvent = convertToMomentJs(pickedStartDate, timezoneOffset)
      .startOf('days')
      .toISOString();

    const filterHolterEvents = {
      studyId,
      profileId,
      types: typesPersistEvent,
      startSearchDate: newEvent.start,
      stopSearchDate: newEvent.stop,
    };

    const holterEvents = await getEventBothSideAddUpdateEvents(filterHolterEvents);
    const combinedEvents = updateEventChangeAndNew(
      holterEvents,
      clonedEventChanges,
      clonedEventNews,
      filterHolterEvents.startSearchDate,
      filterHolterEvents.stopSearchDate,
      typesPersistEvent,
    );
    const { linkEvents, errorEvent, errorMessages } = await processEvent({
      combinedEvents,
      newEvent,
      localBeatChanges,
    });
    if (errorEvent) {
      handleSetError(generateAddEventExistErrorMessage(errorEvent, timezoneOffset));
      return;
    }
    if (errorMessages) {
      handleSetError(errorMessages);
      return;
    }
    newEvent.startTimeEpoch = moment(newEvent.start).startOf('minutes').valueOf();
    newEvent.duration = getEventDuration(newEvent.start, newEvent.stop);
    console.log('[beatHr/addNewEvent]-linkevents', linkEvents);
    if (linkEvents.length) {
      linkEvents.forEach((x) => {
        if (x.isNew) {
          _.remove(clonedEventNews, (eventNew) => eventNew.id === x.id);
        } else if (x.canChangeDuration) {
          const foundEventChanged = clonedEventChanges.find(
            (eventChange) => eventChange.idEvent === x.idEvent,
          );
          if (foundEventChanged) {
            _.assign(foundEventChanged, {
              start: x.start,
              stop: x.stop,
              duration: x.duration,
            });
          } else {
            delete x.canChangeDuration;
            clonedEventChanges.push(x);
          }
        } else {
          // * Gán type delete cho những event được link với new event
          const foundEventChanged = clonedEventChanges.find(
            (eventChange) => eventChange.idEvent === x.idEvent,
          );
          if (foundEventChanged) {
            _.assign(foundEventChanged, { type: TYPE_BEAT_ENUM.DELETED, oldType: x.oldType });
          }
          // else {
          //   clonedEventChanges.push({
          //     ...x,
          //     type: TYPE_BEAT_ENUM.DELETED,
          //     oldType: x.oldType,
          //   });
          // }
        }
      });
      newEvent.linkEvents = linkEvents.filter((x) => !x.isNew);
      clonedEventNews.push(newEvent);
    } else {
      clonedEventNews.push(newEvent);
    }
    const activeButton = getActiveButton();
    if (isRrInterval(activeButton as any)) {
      setCombinedEventsInHeatmap((prev: any) => {
        const clonePrev: any = { ...prev };
        clonePrev[dateContainEvent] = updateEventChangeAndNew(
          holterEvents,
          clonedEventChanges,
          clonedEventNews,
          filterHolterEvents.startSearchDate,
          filterHolterEvents.stopSearchDate,
          [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
        );
        return clonePrev;
      });
    }
    if (activeButton === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
      setSelectedStrip(newEvent);
    }

    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: clonedEventNews,
      eventChangesState: clonedEventChanges,
    }));
    setEventChanges(clonedEventChanges);
    setEventNews(clonedEventNews);
    onClickCancel(true);
  };

  /**
   * Submits the add-event form after checking the picked range. Marks each missing date or time as invalid and flags all four when the start is after the end, aborting on any error; otherwise it builds an AFib or Artifact event from the picked dates and the channel count and delegates to the matching add path, logging and toasting anything thrown.
   *
   * @returns A promise resolving once the event has been added or the submission rejected.
   */
  const onClickAdd = async () => {
    setState({ isSaving: true });
    const clonedErrorObject = { ...state.errorObject };
    if (!state.startDate) {
      _.assign(clonedErrorObject, { startDate: 'Invalid value' });
    } else {
      delete clonedErrorObject.startDate;
    }
    if (!state.endDate) {
      _.assign(clonedErrorObject, { endDate: 'Invalid value' });
    } else {
      delete clonedErrorObject.endDate;
    }
    if (!state.startTime) {
      _.assign(clonedErrorObject, { startTime: 'Invalid value' });
    } else {
      delete clonedErrorObject.startTime;
    }
    if (!state.endTime) {
      _.assign(clonedErrorObject, { endTime: 'Invalid value' });
    } else {
      delete clonedErrorObject.endTime;
    }
    if (state.startDate && state.startTime && state.endDate && state.endTime) {
      if (state.startDate.isAfter(state.endDate)) {
        _.assign(clonedErrorObject, {
          startDate: 'Invalid value',
          endDate: 'Invalid value',
          startTime: 'Invalid value',
          endTime: 'Invalid value',
        });
      } else {
        delete clonedErrorObject.startDate;
        delete clonedErrorObject.endDate;
        delete clonedErrorObject.startTime;
        delete clonedErrorObject.endTime;
      }
    }
    if (!_.isEmpty(clonedErrorObject)) {
      setState({ errorObject: clonedErrorObject, isSaving: false });
      return;
    }

    const { channels } = ecgDataMap;

    try {
      if (state.eventType.value === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
        const newEvent = {
          id: new Date(pickedStartDate).getTime(),
          start: pickedStartDate,
          stop: pickedEndDate,
          type: TYPE_RHYTHM_EVENT_ENUM.AFIB,
          channel: channels.length === 3 ? 2 : 1,
          isNew: true,
        };
        await handleAddNewEventAfib(newEvent);
      } else {
        // * Split artifact because artifact tab not support event overlap multiple day
        // * Need change artifact tab to look like other event tab
        const newEvent = {
          id: new Date(pickedStartDate).getTime(),
          start: pickedStartDate,
          stop: pickedEndDate,
          type: TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
          channel: channels.length === 3 ? 2 : 1,
          isNew: true,
        };
        // const parseStartDate = convertToMomentJs(pickedStartDate, props.timezoneOffset).startOf('days');
        // const parseEndDate = convertToMomentJs(pickedEndDate, props.timezoneOffset).startOf('days');
        // const diffDay = parseEndDate.diff(parseStartDate, 'days');
        // if (diffDay === 0) {
        await handleAddNewEventSingle(newEvent);
        // } else {
        //   await handleAddNewEventMultiple(newEvent);
        // }
      }
    } catch (error) {
      logError('Failed to add new event: ', error);
      toastrError(error.message, 'Error');
      setState({ isSaving: false });
    }
  };

  useUpdateEffect(() => {
    let convertedStartDateMoment;
    let convertedEndDateMoment;
    if (pickedStartDate) {
      convertedStartDateMoment = convertToMomentJs(pickedStartDate, timezone);
    }
    if (pickedEndDate) {
      convertedEndDateMoment = convertToMomentJs(pickedEndDate, timezone);
    }
    const stateObj = {
      startDate: convertedStartDateMoment,
      startTime: convertedStartDateMoment,
      endDate: convertedEndDateMoment,
      endTime: convertedEndDateMoment,
      errorObject: {},
    };
    if (
      startReport !== pickedStartDate ||
      stopReport !== pickedEndDate ||
      state.eventType.value !== TYPE_RHYTHM_EVENT_ENUM.AFIB
    ) {
      _.assign(stateObj, { isUse100Afib: false });
    }
    if (
      pickedStartDate <= startReport &&
      pickedEndDate >= stopReport &&
      state.eventType.value === TYPE_RHYTHM_EVENT_ENUM.AFIB
    ) {
      _.assign(stateObj, { isUse100Afib: true });
    }
    setState(stateObj);
  }, [pickedStartDate, pickedEndDate, startReport, stopReport, state.eventType]);

  useEffect(() => {
    setState({
      eventType:
        _.find(ADD_NEW_EVENT_TYPE_BEAT_HR_TAB_OPTIONS, (x) => x.value === activeButton) ||
        ADD_NEW_EVENT_TYPE_BEAT_HR_TAB_OPTIONS[1],
    });
  }, [activeButton]);

  useEffect(() => {
    if (!controlModal.isShowAddNewEvent) {
      managerAPI.cancelStore(KEY_CANCEL.API_MODAL);
    }
  }, [controlModal.isShowAddNewEvent]);

  return {
    activeButton,
    state,
    onChangeEvent,
    onChangeCheckbox,
    onChange,
    onClickCancel,
    onClickAdd,
  };
};

export default useAddNewEvent;
